import React, { useState, useEffect, useRef } from 'react';
import { css } from '@emotion/css';
import { Flex } from '@components/Flex';
import { Spacing } from '@components/Spacing';
import { Loader } from '@components/Loader';
import { ErrorMessage } from '@components/ErrorMessage';
import { fetchStrokeDataSessions, fetchStrokeDataSession, StrokeDataSession } from '@remotes/fetchStrokeDataSessions';
import { deleteStrokeDataSession } from '@remotes/deleteStrokeDataSession';
import { StrokeDataCanvas } from '@components/StrokeDataCanvas';
import { showSuccessMsg } from '@utils/notification';

interface GroupedStrokeData {
  [userId: string]: StrokeDataSession[];
}

const StrokeDataViewer: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [strokeSessions, setStrokeSessions] = useState<StrokeDataSession[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());
  const [loadingSessionIds, setLoadingSessionIds] = useState<Set<number>>(new Set());
  const loadedSessionIds = useRef<Set<number>>(new Set()); // Track which sessions have been loaded/attempted

  useEffect(() => {
    const loadStrokeSessions = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const sessions = await fetchStrokeDataSessions();
        console.log('Loaded stroke sessions:', sessions.length);
        
        setStrokeSessions(sessions);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching stroke data sessions:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch stroke data');
        setLoading(false);
      }
    };

    loadStrokeSessions();
  }, []);

  // Load individual session details when a user is selected
  useEffect(() => {
    if (!selectedUserId) return;

    const userSessions = strokeSessions.filter(s => 
      (s.username || s.user) === selectedUserId
    );

    const loadSessionDetails = async () => {
      // Find sessions that don't have stroke_data loaded yet and haven't been attempted
      const sessionsToLoad = userSessions.filter(s => 
        !s.stroke_data && !loadedSessionIds.current.has(s.id)
      );
      
      if (sessionsToLoad.length === 0) return;

      console.log(`Loading ${sessionsToLoad.length} sessions in parallel for user ${selectedUserId}`);

      // Mark sessions as attempted to prevent duplicate loads
      sessionsToLoad.forEach(session => loadedSessionIds.current.add(session.id));

      // Mark all sessions as loading upfront to avoid showing "Failed to load" before fetching starts
      setLoadingSessionIds(prev => {
        const newSet = new Set(prev);
        sessionsToLoad.forEach(session => newSet.add(session.id));
        return newSet;
      });

      // Load all sessions in parallel using Promise.all
      const loadPromises = sessionsToLoad.map((session) => {
        const startTime = performance.now();
        console.log(`🚀 Starting fetch for session ${session.id}`);
        
        return fetchStrokeDataSession(session.id)
          .then((fullSession) => {
            const endTime = performance.now();
            console.log(`✅ Completed session ${session.id} in ${Math.round(endTime - startTime)}ms`);
            
            // Update the session in state with the full data
            setStrokeSessions(prev => 
              prev.map(s => s.id === session.id ? fullSession : s)
            );
            
            // Remove from loading state
            setLoadingSessionIds(prev => {
              const newSet = new Set(prev);
              newSet.delete(session.id);
              return newSet;
            });
            
            return { status: 'fulfilled', sessionId: session.id };
          })
          .catch((err) => {
            const endTime = performance.now();
            console.error(`❌ Failed session ${session.id} in ${Math.round(endTime - startTime)}ms:`, err);
            
            // Remove from loading state
            setLoadingSessionIds(prev => {
              const newSet = new Set(prev);
              newSet.delete(session.id);
              return newSet;
            });
            
            return { status: 'rejected', sessionId: session.id, error: err };
          });
      });

      // Wait for all sessions to complete (successful or failed)
      const results = await Promise.allSettled(loadPromises);
      
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      console.log(`Loaded ${successful} sessions successfully, ${failed} failed for user ${selectedUserId}`);
    };

    loadSessionDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]); // Intentionally omit strokeSessions to prevent infinite re-renders

  const handleDeleteSession = async (sessionId: number) => {
    if (!window.confirm('Are you sure you want to delete this stroke session? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingIds(prev => new Set(prev).add(sessionId));
      await deleteStrokeDataSession(sessionId);
      
      // Remove from local state and tracking
      setStrokeSessions(prev => prev.filter(s => s.id !== sessionId));
      loadedSessionIds.current.delete(sessionId);
      showSuccessMsg('Stroke session deleted successfully');
      
      // If we just deleted the last session for the selected user, go back
      const remainingSessions = strokeSessions.filter(s => s.id !== sessionId);
      const selectedUserKey = selectedUserId;
      if (selectedUserKey) {
        const userSessions = remainingSessions.filter(s => 
          (s.username || s.user) === selectedUserKey
        );
        if (userSessions.length === 0) {
          setSelectedUserId(null);
        }
      }
    } catch (err) {
      console.error('Error deleting stroke session:', err);
      alert(err instanceof Error ? err.message : 'Failed to delete stroke session');
    } finally {
      setDeletingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };

  const groupedData = React.useMemo(() => {
    return strokeSessions.reduce((acc: GroupedStrokeData, session) => {
      const key = session.username || session.user;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(session);
      return acc;
    }, {});
  }, [strokeSessions]);

  const sortedUsernames = React.useMemo(() => {
    return Object.keys(groupedData).sort((a, b) => {
      // Sort by most recent session
      const aLatest = Math.max(...groupedData[a].map(s => new Date(s.created_at).getTime()));
      const bLatest = Math.max(...groupedData[b].map(s => new Date(s.created_at).getTime()));
      return bLatest - aLatest;
    });
  }, [groupedData]);

  if (loading) {
    return (
      <Flex direction="column" align="center" className={containerStyle}>
        <Spacing size={40} />
        <Loader />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction="column" align="center" className={containerStyle}>
        <Spacing size={40} />
        <ErrorMessage text={error} />
      </Flex>
    );
  }

  if (strokeSessions.length === 0) {
    return (
      <Flex direction="column" align="center" className={containerStyle}>
        <Spacing size={40} />
        <div className={emptyStateStyle}>
          <p>No stroke data sessions found</p>
        </div>
      </Flex>
    );
  }

  // Show user directory view
  if (!selectedUserId) {
    return (
      <Flex direction="column" align="center" className={containerStyle}>
        <Spacing size={40} />
        <h2 className={titleStyle}>Stroke Data Sessions by User</h2>
        <Spacing size={20} />
        <div className={userListStyle}>
          {sortedUsernames.map((username) => {
            const sessions = groupedData[username];
            const sessionCount = sessions.length;
            const latestDate = new Date(
              Math.max(...sessions.map(s => new Date(s.created_at).getTime()))
            );

            return (
              <button
                key={username}
                className={userCardStyle}
                onClick={() => setSelectedUserId(username)}
              >
                <div className={userCardContentStyle}>
                  <div className={userIdStyle}>
                    {username}
                  </div>
                  <div className={userStatsStyle}>
                    <span>{sessionCount} session{sessionCount !== 1 ? 's' : ''}</span>
                    <span className={dateTextStyle}>
                      Last: {formatDate(latestDate.toISOString().split('T')[0])}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </Flex>
    );
  }

  // Show collection view for selected user
  const userSessions = groupedData[selectedUserId];
  const groupedByDate = groupSessionsByDate(userSessions);

  return (
    <Flex direction="column" align="center" className={containerStyle}>
      <Spacing size={40} />
      <div className={headerContainerStyle}>
        <button
          className={backButtonStyle}
          onClick={() => setSelectedUserId(null)}
        >
          ← Back to Users
        </button>
        <h2 className={titleStyle}>
          {selectedUserId}
        </h2>
        <div className={sessionCountStyle}>
          {userSessions.length} session{userSessions.length !== 1 ? 's' : ''}
        </div>
      </div>
      <Spacing size={40} />
      
      {Object.entries(groupedByDate).map(([date, sessions]) => (
        <div key={date} className={dateGroupStyle}>
          <div className={dateHeaderStyle}>{formatDate(date)}</div>
          <div className={sessionGridStyle}>
            {sessions.map((session) => {
              const isLoading = loadingSessionIds.has(session.id);
              const hasStrokeData = session.stroke_data !== null && session.stroke_data !== undefined;
              
              return (
                <div key={session.id} className={sessionCardWrapperStyle}>
                  <div className={sessionCardStyle}>
                    {isLoading ? (
                      <div className={loadingPlaceholderStyle}>
                        <Loader />
                      </div>
                    ) : hasStrokeData ? (
                      <StrokeDataCanvas
                        strokeData={session.stroke_data ?? null}
                        width={225}
                        height={225}
                        className={canvasCardStyle}
                      />
                    ) : (
                      <div className={errorPlaceholderStyle}>
                        <span>Failed to load</span>
                      </div>
                    )}
                    {!isLoading && (
                      <button
                        className={deleteButtonStyle}
                        onClick={() => handleDeleteSession(session.id)}
                        disabled={deletingIds.has(session.id)}
                        aria-label="Delete session"
                        title="Delete session"
                      >
                        {deletingIds.has(session.id) ? '...' : '×'}
                      </button>
                    )}
                  </div>
                  <div className={sessionInfoStyle}>
                    <div className={sessionIdStyle}>ID: {session.id}</div>
                    <div className={sessionTimeStyle}>
                      {new Date(session.created_at).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </Flex>
  );
};

const groupSessionsByDate = (sessions: StrokeDataSession[]) => {
  return sessions.reduce((acc: { [key: string]: StrokeDataSession[] }, session) => {
    const date = session.created_at.split('T')[0];
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(session);
    return acc;
  }, {});
};

const formatDate = (dateString: string) => {
  const date = new Date(`${dateString}T00:00:00`);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  const isYesterday =
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();

  if (isToday) return 'Today';
  if (isYesterday) return 'Yesterday';

  return dateString;
};

const containerStyle = css`
  width: 100%;
  padding: 0 20px 64px;
  box-sizing: border-box;
`;

const titleStyle = css`
  font-size: 28px;
  font-weight: 600;
  color: #ffffff;
  text-align: center;
  margin: 0;
`;

const emptyStateStyle = css`
  text-align: center;
  color: #aaaaaa;
  font-size: 16px;
  
  p {
    margin: 0;
  }
`;

const userListStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  width: 100%;
  max-width: 1200px;
  padding: 0 16px;
`;

const userCardStyle = css`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const userCardContentStyle = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const userIdStyle = css`
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
`;

const userStatsStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14px;
  color: #aaaaaa;
`;

const dateTextStyle = css`
  font-size: 12px;
  color: #888888;
`;

const headerContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const backButtonStyle = css`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px 20px;
  color: #aaaaaa;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: #ffffff;
  }
`;

const sessionCountStyle = css`
  font-size: 14px;
  color: #aaaaaa;
`;

const dateGroupStyle = css`
  width: 100%;
  max-width: 1200px;
  margin-bottom: 60px;
`;

const dateHeaderStyle = css`
  font-size: 18px;
  font-weight: 400;
  color: #aaaaaa;
  margin-bottom: 16px;
  padding: 0 16px;
`;

const sessionGridStyle = css`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  padding: 0 16px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const sessionCardWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const sessionCardStyle = css`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  
  &:hover button {
    opacity: 1;
  }
`;

const canvasCardStyle = css`
  width: 100%;
  height: auto;
  aspect-ratio: 1 / 1;
`;

const sessionInfoStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 4px;
`;

const sessionIdStyle = css`
  font-size: 12px;
  color: #888888;
`;

const sessionTimeStyle = css`
  font-size: 12px;
  color: #888888;
`;

const deleteButtonStyle = css`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 59, 48, 0.9);
  border: none;
  color: white;
  font-size: 24px;
  font-weight: 300;
  line-height: 1;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  
  &:hover {
    background: rgba(255, 59, 48, 1);
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  @media (max-width: 600px) {
    opacity: 1;
  }
`;

const loadingPlaceholderStyle = css`
  width: 225px;
  height: 225px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const errorPlaceholderStyle = css`
  width: 225px;
  height: 225px;
  background: rgba(255, 59, 48, 0.1);
  border: 1px solid rgba(255, 59, 48, 0.3);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff3b30;
  font-size: 14px;
`;

export default StrokeDataViewer;

